/**
 * Migration utility for converting localStorage data to IndexedDB.
 *
 * Migrates existing character data from:
 * - 'character-draft' (characterCreationStore)
 * - 'play-session-storage' (playSessionStore)
 *
 * Features:
 * - Creates backup before clearing localStorage
 * - Idempotent (safe to run multiple times)
 * - Graceful error handling
 */
import { saveCharacter, isIndexedDBSupported } from './characterDatabase';
import type { CharacterDraft } from '../stores/types/characterDraft';
import type { RuntimeState } from './characterExport';

// ============================================================================
// Constants
// ============================================================================

const CHARACTER_DRAFT_KEY = 'character-draft';
const PLAY_SESSION_KEY = 'play-session-storage';
const MIGRATION_BACKUP_KEY = 'character-catalog-migration-backup';
const MIGRATION_COMPLETE_KEY = 'character-catalog-migration-complete';

// ============================================================================
// Types
// ============================================================================

/**
 * Structure of the persisted character draft store in localStorage.
 */
interface PersistedCharacterDraftStore {
  state: {
    draft: CharacterDraft;
  };
  version?: number;
}

/**
 * Structure of a play session from the store.
 */
interface PersistedPlaySession {
  characterId: string;
  currentHp: number;
  maxHp: number;
  tempHp: number;
  deathSaveSuccesses: number;
  deathSaveFailures: number;
  spellSlots: Record<string, { max: number; used: number }>;
  hitDice: Record<string, { max: number; used: number }>;
  classResources: Record<string, { max: number; used: number }>;
  activeConditions: string[];
  exhaustionLevel: number;
  concentrationSpell: string | null;
  sessionNotes: string;
}

/**
 * Structure of the persisted play session store in localStorage.
 */
interface PersistedPlaySessionStore {
  state: {
    activeSession: PersistedPlaySession | null;
  };
  version?: number;
}

/**
 * Result of the migration process.
 */
export interface MigrationResult {
  success: boolean;
  migrated: boolean;
  characterId?: string;
  characterName?: string;
  hadRuntimeState: boolean;
  warnings: string[];
  errors: string[];
}

// ============================================================================
// Migration Logic
// ============================================================================

/**
 * Check if migration has already been completed.
 */
export function isMigrationComplete(): boolean {
  try {
    return localStorage.getItem(MIGRATION_COMPLETE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Check if there is data to migrate.
 */
export function hasDataToMigrate(): boolean {
  try {
    const characterData = localStorage.getItem(CHARACTER_DRAFT_KEY);
    return !!characterData;
  } catch {
    return false;
  }
}

/**
 * Parse the localStorage character draft data.
 */
function parseCharacterDraft(): CharacterDraft | null {
  try {
    const raw = localStorage.getItem(CHARACTER_DRAFT_KEY);
    if (!raw) return null;

    const parsed: PersistedCharacterDraftStore = JSON.parse(raw);
    if (!parsed.state?.draft) return null;

    return parsed.state.draft;
  } catch {
    return null;
  }
}

/**
 * Parse the localStorage play session data.
 */
function parsePlaySession(): PersistedPlaySession | null {
  try {
    const raw = localStorage.getItem(PLAY_SESSION_KEY);
    if (!raw) return null;

    const parsed: PersistedPlaySessionStore = JSON.parse(raw);
    if (!parsed.state?.activeSession) return null;

    return parsed.state.activeSession;
  } catch {
    return null;
  }
}

/**
 * Convert a play session to RuntimeState format.
 */
function sessionToRuntimeState(session: PersistedPlaySession): RuntimeState {
  return {
    hitPoints: {
      current: session.currentHp,
      max: session.maxHp,
      temporary: session.tempHp,
    },
    deathSaves: {
      successes: session.deathSaveSuccesses,
      failures: session.deathSaveFailures,
    },
    spellSlots: session.spellSlots,
    hitDice: session.hitDice,
    classResources: session.classResources,
    conditions: session.activeConditions,
    exhaustionLevel: session.exhaustionLevel,
    concentrationSpell: session.concentrationSpell,
    sessionNotes: session.sessionNotes,
  };
}

/**
 * Create a backup of localStorage data before migration.
 */
function createBackup(): void {
  const backup: Record<string, string | null> = {
    [CHARACTER_DRAFT_KEY]: localStorage.getItem(CHARACTER_DRAFT_KEY),
    [PLAY_SESSION_KEY]: localStorage.getItem(PLAY_SESSION_KEY),
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(MIGRATION_BACKUP_KEY, JSON.stringify(backup));
}

/**
 * Clear the old localStorage keys after successful migration.
 */
function clearOldLocalStorage(): void {
  localStorage.removeItem(CHARACTER_DRAFT_KEY);
  localStorage.removeItem(PLAY_SESSION_KEY);
}

/**
 * Mark migration as complete.
 */
function markMigrationComplete(): void {
  localStorage.setItem(MIGRATION_COMPLETE_KEY, 'true');
}

/**
 * Migrate existing localStorage data to IndexedDB.
 *
 * This function is idempotent - it's safe to call multiple times.
 * It will skip migration if:
 * - Migration has already been completed
 * - There's no data to migrate
 * - IndexedDB is not supported
 */
export async function migrateLocalStorageToIndexedDB(): Promise<MigrationResult> {
  const result: MigrationResult = {
    success: true,
    migrated: false,
    hadRuntimeState: false,
    warnings: [],
    errors: [],
  };

  // Check if already migrated
  if (isMigrationComplete()) {
    result.warnings.push('Migration already completed.');
    return result;
  }

  // Check IndexedDB support
  if (!isIndexedDBSupported()) {
    result.success = false;
    result.errors.push('IndexedDB is not supported in this browser.');
    return result;
  }

  // Check for data to migrate
  if (!hasDataToMigrate()) {
    // No data to migrate - mark as complete so we don't check again
    markMigrationComplete();
    result.warnings.push('No character data found to migrate.');
    return result;
  }

  // Parse the character draft
  const draft = parseCharacterDraft();
  if (!draft) {
    result.success = false;
    result.errors.push('Failed to parse character draft from localStorage.');
    return result;
  }

  // Validate required fields
  if (!draft.rulesetId) {
    result.success = false;
    result.errors.push('Character draft is missing rulesetId.');
    return result;
  }

  // Create backup before any modifications
  try {
    createBackup();
  } catch {
    result.warnings.push('Failed to create backup (continuing anyway).');
  }

  // Parse play session if it exists and matches the character
  const session = parsePlaySession();
  let runtimeState: RuntimeState | undefined;

  if (session) {
    // Check if the session belongs to this character
    if (session.characterId === draft.id) {
      runtimeState = sessionToRuntimeState(session);
      result.hadRuntimeState = true;
    } else {
      result.warnings.push(
        'Play session found but belongs to a different character.'
      );
    }
  }

  // Save to IndexedDB
  const saveResult = await saveCharacter(draft, runtimeState);
  if (!saveResult.success) {
    result.success = false;
    result.errors.push(...saveResult.errors);
    return result;
  }

  // Success! Clean up localStorage
  result.migrated = true;
  result.characterId = saveResult.data?.id;
  result.characterName = saveResult.data?.name;

  try {
    clearOldLocalStorage();
  } catch {
    result.warnings.push('Failed to clear old localStorage (data migrated successfully).');
  }

  markMigrationComplete();

  return result;
}

/**
 * Get the migration backup (if it exists).
 */
export function getMigrationBackup(): Record<string, string | null> | null {
  try {
    const raw = localStorage.getItem(MIGRATION_BACKUP_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Clear the migration backup.
 */
export function clearMigrationBackup(): void {
  localStorage.removeItem(MIGRATION_BACKUP_KEY);
}

/**
 * Reset migration state (primarily for testing).
 */
export function resetMigrationState(): void {
  localStorage.removeItem(MIGRATION_COMPLETE_KEY);
  localStorage.removeItem(MIGRATION_BACKUP_KEY);
}
